import { useEffect, useRef } from 'react';

// CRT glitch backdrop · grid + sweep + tear bands + datablocks + scanlines + vignette.
// Portado del prototipo Claude Design (canvas-bg.jsx · drawGlitch). Color brand fijo
// #66e0ff por decisión de diseño — el login es siempre tema oscuro.

type Props = {
  intensity?: number; // 0..1
  zoom?: number; // 1 = neutro
};

const INK = '#0a0f12';
const INK_HI = '#0e1419';

export function CrtCanvas({ intensity = 0.5, zoom = 1 }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const propsRef = useRef({ intensity, zoom });

  // Sync live props para que el rAF lea siempre el último valor sin remontar
  // el canvas (zoom cambia frame por frame durante el intro).
  useEffect(() => {
    propsRef.current = { intensity, zoom };
  });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let running = true;
    let t = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(2, r.width * dpr);
      canvas.height = Math.max(2, r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const tick = () => {
      if (!running) return;
      t += 0.016;
      const { intensity: it, zoom: zm } = propsRef.current;
      const r = canvas.getBoundingClientRect();
      const w = r.width;
      const h = r.height;

      // Backdrop · ink + radial elevation
      ctx.fillStyle = INK;
      ctx.fillRect(0, 0, w, h);
      const bg = ctx.createRadialGradient(
        w / 2,
        h / 2,
        0,
        w / 2,
        h / 2,
        Math.max(w, h) * 0.7,
      );
      bg.addColorStop(0, INK_HI);
      bg.addColorStop(1, INK);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.scale(zm, zm);
      ctx.translate(-w / 2, -h / 2);

      const a = Math.max(0.05, Math.min(1, it));
      drawGlitch(ctx, w, h, t, a);

      ctx.restore();

      drawScanlines(ctx, w, h, a);
      drawVignette(ctx, w, h);

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
}

function drawGlitch(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  t: number,
  a: number,
) {
  // Grid base
  ctx.strokeStyle = `rgba(102, 224, 255, ${0.06 * a})`;
  ctx.lineWidth = 1;
  const step = 40;
  for (let x = 0; x < w; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = 0; y < h; y += step) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  // Sweeping bright line
  const sweepY = ((t * 60) % (h + 200)) - 100;
  const sgrad = ctx.createLinearGradient(0, sweepY - 60, 0, sweepY + 60);
  sgrad.addColorStop(0, 'rgba(102, 224, 255, 0)');
  sgrad.addColorStop(0.5, `rgba(102, 224, 255, ${0.2 * a})`);
  sgrad.addColorStop(1, 'rgba(102, 224, 255, 0)');
  ctx.fillStyle = sgrad;
  ctx.fillRect(0, sweepY - 60, w, 120);

  // Random tear bands (seeded por tiempo discretizado)
  const seed = Math.floor(t * 3);
  for (let i = 0; i < 4; i++) {
    const s = (seed * 9301 + i * 49297) % 233280;
    const rnd = (s % 1000) / 1000;
    if (rnd > 0.55) continue;
    const y = rnd * h;
    const bh = 4 + rnd * 18;
    ctx.fillStyle = `rgba(102, 224, 255, ${(0.05 + rnd * 0.18) * a})`;
    ctx.fillRect(0, y, w, bh);
    ctx.fillStyle = `rgba(255, 90, 90, ${rnd * 0.06 * a})`;
    ctx.fillRect(rnd * 12, y, w, bh * 0.6);
  }

  // Data blocks · corners
  ctx.font = '11px "JetBrains Mono", monospace';
  ctx.fillStyle = `rgba(102, 224, 255, ${0.4 * a})`;
  const tHex = (Math.floor(t * 1000) % 0xffff)
    .toString(16)
    .padStart(4, '0')
    .toUpperCase();
  const blocks: Array<[number, number, string]> = [
    [20, 22, `T+${t.toFixed(2)}s`],
    [20, 38, `0x${tHex}`],
    [w - 140, 22, `SYNC ${Math.floor(t * 7) % 100}%`],
    [w - 140, 38, `BUFFER OK`],
    [20, h - 24, `>> AAL2 · TOTP`],
    [w - 140, h - 24, `LAT 12ms`],
  ];
  blocks.forEach(([x, y, s]) => ctx.fillText(s, x, y));
}

function drawScanlines(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  a: number,
) {
  ctx.save();
  ctx.globalAlpha = 0.11 * a;
  ctx.fillStyle = 'rgba(0,0,0,1)';
  for (let y = 0; y < h; y += 3) {
    ctx.fillRect(0, y, w, 1);
  }
  ctx.restore();
}

function drawVignette(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const g = ctx.createRadialGradient(
    w / 2,
    h / 2,
    Math.min(w, h) * 0.25,
    w / 2,
    h / 2,
    Math.max(w, h) * 0.78,
  );
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(0.7, 'rgba(0,0,0,0.45)');
  g.addColorStop(1, 'rgba(0,0,0,0.85)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}
